// ============================================================
// Datei: src/pages/deployment/Deployment.jsx
//
// PURPOSE:
// - Hauptseite für Helfer/Superhelfer Deployment
// - lädt verfügbare Schichten
// - lädt eigene Schichten
// - lädt Salary-/Badge-Infos
// - steuert Einschreiben und Austragen
//
// ÄNDERUNGEN:
// - MAX_ASSIGNED_SHIFTS bleibt auf 10
// - robustes Error-Handling bei API-Calls ergänzt
// - Optimistic UI aktualisiert publicSlots UND availableSlots
// - bestehende Desktop-/Mobile-Komponenten bleiben unverändert
// ============================================================

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Api from "../../Api";
import roleMap from "../../utils/roleUtils";
import useMediaQuery from "../../hooks/useMediaQuery";

import DeploymentDesktop from "./DeploymentDesktop";
import DeploymentMobile from "./DeploymentMobile";
import ConfirmJoinShiftModal from "../../components/modals/ConfirmJoinShiftModal";

import "./deployment.css";

const MAX_ASSIGNED_SHIFTS = 6;

const Deployment = () => {

	const isMobile = useMediaQuery("(max-width: 768px)");

	const [shifts, setShifts] = useState([]);
	const [assignedShifts, setAssignedShifts] = useState([]);
	const [salaryInfo, setSalaryInfo] = useState([]);
	const [badgeInfo, setBadgeInfo] = useState([]);

	const [pendingShiftId, setPendingShiftId] = useState(null);
	const [showConfirm, setShowConfirm] = useState(false);
	const [isJoining, setIsJoining] = useState(false);

	// ============================================================
	// JOIN LOCK
	// Verhindert doppelte Klicks / doppelte Requests während
	// eines laufenden Einschreibevorgangs.
	// ============================================================
	const joinLockRef = useRef(false);

	const navigate = useNavigate();

	// ------------------------------------------------------------
	// LOADERS
	// ------------------------------------------------------------

	const loadShifts = async () => {
		if (joinLockRef.current) return;

		try {
			const res = await Api.get("/shifts");

			if (Array.isArray(res.data)) {
				setShifts(res.data.map(s => ({ ...s })));
			} else {
				setShifts([]);
			}
		} catch (err) {
			console.error("Fehler beim Laden der verfügbaren Schichten:", err);
			setShifts([]);
		}
	};

	const loadAssigned = async () => {
		try {
			const res = await Api.get("/deployments/volunteers/shifts");

			const assigned = Array.isArray(res.data) ? res.data : [];
			setAssignedShifts(assigned);

			const sal = assigned
				.filter(s => s.salary)
				.map(s => ({
					department: s.department,
					admission: s.salary.admission,
					amount: s.salary.amount
				}));

			setSalaryInfo(sal);
		} catch (err) {
			console.error("Fehler beim Laden der eigenen Schichten:", err);
			setAssignedShifts([]);
			setSalaryInfo([]);
		}
	};

	const loadBadges = async (roleId) => {
		if (!roleId) return;

		try {
			const res = await Api.get(`/badges/role/${roleId}`);
			setBadgeInfo(Array.isArray(res.data) ? res.data : []);
		} catch (err) {
			console.error("Fehler beim Laden der Badges:", err);
			setBadgeInfo([]);
		}
	};

	// ------------------------------------------------------------
	// INIT + POLLING
	// ------------------------------------------------------------

	useEffect(() => {
		const role = localStorage.getItem("role");
		const roleId = roleMap[role];

		if (role !== "HELFER" && role !== "SUPERHELFER") {
			alert("Access denied!");
			navigate("/home");
			return;
		}

		loadShifts();
		loadAssigned();
		loadBadges(roleId);

		const interval = setInterval(loadShifts, 10000);

		return () => clearInterval(interval);
	}, [navigate]);

	// ------------------------------------------------------------
	// JOIN FLOW
	// ------------------------------------------------------------

	const handleJoinShift = (shiftId) => {
		// Maximal 10 Schichten erlaubt.
		// Wichtig:
		// Diese Zahl muss auch in AssignedShifts, ShiftOverview
		// und TimelineShiftOverview gleich gesetzt sein.
		if (assignedShifts.length >= MAX_ASSIGNED_SHIFTS) return;

		setPendingShiftId(shiftId);
		setShowConfirm(true);
	};

	const confirmJoinShift = async () => {
		if (!pendingShiftId || joinLockRef.current) return;

		joinLockRef.current = true;
		setIsJoining(true);

		// ============================================================
		// OPTIMISTIC UI
		// Reduziert den Slot lokal sofort.
		// Danach werden echte Daten vom Backend neu geladen.
		// ============================================================
		setShifts(prev =>
			prev.map(s => {
				if (s.shiftId !== pendingShiftId) {
					return s;
				}

				const currentPublicSlots = Number(s.publicSlots ?? s.availableSlots ?? 0);
				const currentAvailableSlots = Number(s.availableSlots ?? s.publicSlots ?? 0);

				return {
					...s,
					publicSlots: Math.max(0, currentPublicSlots - 1),
					availableSlots: Math.max(0, currentAvailableSlots - 1)
				};
			})
		);

		try {
			await Api.post(`/shifts/${pendingShiftId}/enroll`);
			await Promise.all([loadAssigned(), loadShifts()]);
		} catch (err) {
			console.error("Fehler beim Einschreiben in die Schicht:", err);

			// Bei Fehler werden die echten Backend-Daten neu geladen,
			// damit der optimistische lokale Zustand korrigiert wird.
			await loadShifts();
		} finally {
			joinLockRef.current = false;
			setIsJoining(false);
			setPendingShiftId(null);
			setShowConfirm(false);
		}
	};

	const leaveShift = async (shiftId) => {
		try {
			await Api.delete(`/shifts/${shiftId}/unenroll`);
			await Promise.all([loadAssigned(), loadShifts()]);
		} catch (err) {
			console.error("Fehler beim Austragen aus der Schicht:", err);
		}
	};

	const pendingShift = useMemo(
		() => shifts.find(s => s.shiftId === pendingShiftId),
		[shifts, pendingShiftId]
	);

	// ------------------------------------------------------------
	// RENDER
	// ------------------------------------------------------------

	return (
		<>
			{isMobile ? (
				<DeploymentMobile
					shifts={shifts}
					assignedShifts={assignedShifts}
					salaryInfo={salaryInfo}
					badgeInfo={badgeInfo}
					handleJoinShift={handleJoinShift}
					leaveShift={leaveShift}
				/>
			) : (
				<DeploymentDesktop
					shifts={shifts}
					assignedShifts={assignedShifts}
					salaryInfo={salaryInfo}
					badgeInfo={badgeInfo}
					isJoining={isJoining}
					showConfirm={showConfirm}
					handleJoinShift={handleJoinShift}
					confirmJoinShift={confirmJoinShift}
					setShowConfirm={setShowConfirm}
					leaveShift={leaveShift}
				/>
			)}

			{showConfirm && pendingShift && (
				<ConfirmJoinShiftModal
					shift={pendingShift}
					onConfirm={confirmJoinShift}
					onCancel={() => {
						setShowConfirm(false);
						setPendingShiftId(null);
					}}
				/>
			)}
		</>
	);
};

export default Deployment;